import ModalViewModel from 'infra/ModalViewModel'
import { action, computed, makeObservable, observable } from 'mobx'
import { required, validationContext } from 'validx'
import {
  between,
  greaterThanOrEqual,
  lengthBetween,
} from 'utils/validation-utils'
import { Task } from 'mobx-task'
import InventoryItem, {
  InventoryItemBookkeepingTypeSpecification,
  InventoryItemConsultationTypeSpecification,
  InventoryItemDefaultTypeSpecification,
  InventoryItemExtensionTypeSpecification,
  InventoryItemPricing,
  InventoryItemSupportingItem,
  InventoryItemTaxTypeSpecification,
  InventoryItemType,
  InventoryItemTypeSpecification,
} from '../../InventoryItem'
import ManageSupportingItemsViewModel from '../../components/InventoryItemForm/ManageSupportingItemsModal/ManageSupportingItemsViewModel'
import { SupportingItemsPorts } from '../../../ports'
import { FeeEditorViewModel } from './FeeEditorViewModel'
import { FlatEditorViewModel } from './FlatEditorViewModel'
import { MarkdownEditorViewModel } from './MarkdownEditorViewModel'
import { MarkupEditorViewModel } from './MarkupEditorViewModel'
import {
  IInventoryItemPricingEditor,
  InventoryItemPricingValues,
} from './inventory-item-pricing-editor'
import { ExtensionBillingViewModel } from '../../components/InventoryItemForm/ManageExtensionBillingInfo/ExtensionBillingViewModel'

/**
 * Base View Model for handling Inventory Item operations.
 */
export abstract class BaseInventoryItemViewModel extends ModalViewModel {
  /**
   * A selected Inventory Item. Used for edit/override operations.
   */
  @observable
  item?: InventoryItem = undefined

  /**
   * Holds the maximum lengths for each field.
   */
  private readonly fieldLengths = {
    name: { min: 1, max: 100 },
    description: { min: 1, max: 280 },
    consultationLength: { min: 1, max: 180 },
  } as const

  /**
   * The name for the Inventory Item.
   */
  @observable
  name = ''

  /**
   * The description for the Inventory Item.
   */
  @observable
  description = ''

  /**
   * The courtesy quantity for the Inventory Item. This represents how many times
   * the item may be applied before it starts counting towards the job price for
   * the client.
   */
  @observable
  courtesyQuantity = 0

  /**
   * The selected inventory item type specification.
   */
  @observable
  itemType: InventoryItemType = InventoryItemType.Default

  /**
   * The length of the consultation in minutes.
   */
  @observable
  consultationLength = 0

  /**
   * The client price for the review.
   */
  @observable
  reviewPrice = 0

  /**
   * The pro payout for the review.
   */
  @observable
  reviewPayout = 0

  /**
   * The selected inventory item price type.
   */
  @observable
  itemPriceType = InventoryItemPricing.Flat

  @observable
  supportingItems: InventoryItemSupportingItem[] = []

  /**
   * Whether to hide the inventory item from pro.
   */
  @observable
  hideFromPro: boolean = false

  /**
   * Whether the item should be added to the scope at the end of every bookkeeping cycle.
   */
  @observable
  recurring: boolean = false

  manageSupportingItemsViewModel = new ManageSupportingItemsViewModel(
    this.supportingItemsAdapter,
    () => this.getSupportingItems,
    this.setSupportingItems,
  )

  /**
   * The various editor view models.
   */
  readonly pricingVMs = {
    [InventoryItemPricing.Fee]: new FeeEditorViewModel(),
    [InventoryItemPricing.Flat]: new FlatEditorViewModel(),
    [InventoryItemPricing.Markup]: new MarkupEditorViewModel(),
    [InventoryItemPricing.Markdown]: new MarkdownEditorViewModel(),
  } as const

  /**
   * Validation context for this view model.
   */
  validation = validationContext(this, {
    name: [
      required('A name is required.'),
      lengthBetween(
        this.fieldLengths.name.min,
        this.fieldLengths.name.max,
        `Name must be between ${this.fieldLengths.name.min} and ${this.fieldLengths.name.max} characters.`,
      ),
    ],
    description: [
      required('A description is required.'),
      lengthBetween(
        this.fieldLengths.description.min,
        this.fieldLengths.description.max,
        `Description must be between ${this.fieldLengths.description.min} and ${this.fieldLengths.description.max} characters.`,
      ),
    ],
    courtesyQuantity: [
      required('A courtesy quantity is required.'),
      greaterThanOrEqual(0, 'Must be zero or greater.'),
    ],
    consultationLength: [
      (ctx) =>
        this.isConsultation
          ? between(
              this.fieldLengths.consultationLength.min,
              this.fieldLengths.consultationLength.max,
              'The consultation length must be between 1 to 180 minutes.',
            )(ctx)
          : true,
    ],
    reviewPrice: [
      (ctx) =>
        this.isTax
          ? greaterThanOrEqual(0, 'Review price must be zero or greater.')(ctx)
          : true,
    ],
    reviewPayout: [
      (ctx) =>
        this.isTax
          ? greaterThanOrEqual(
              0,
              'Review Pro payout must be zero or greater.',
            )(ctx)
          : true,
    ],
  })

  protected constructor(private supportingItemsAdapter: SupportingItemsPorts) {
    super()
    makeObservable(this)
  }

  /**
   * The supporting items for the Inventory Item.
   */
  @computed
  get getSupportingItems() {
    return this.supportingItems
  }

  /**
   * Gets the active Pricing View Model for the Inventory Item.
   */
  @computed
  get pricingViewModel(): IInventoryItemPricingEditor {
    return this.pricingVMs[this.itemPriceType]
  }

  /**
   * Computes the number of remaining characters for each field
   */
  @computed
  get remainingCharactersIndicator() {
    const makeIndicator = (max: number, current: number) =>
      current !== 0 ? `${max - current} characters remaining` : ''

    return {
      name: makeIndicator(this.fieldLengths.name.max, this.name.length),
      description: makeIndicator(
        this.fieldLengths.description.max,
        this.description.length,
      ),
    }
  }

  /**
   * Whether the selected inventory item type is default.
   */
  @computed
  get isDefault() {
    return this.itemType === InventoryItemType.Default
  }

  /**
   * Whether the selected inventory item type is bookkeeping.
   */
  @computed
  get isBookkeeping() {
    return this.itemType === InventoryItemType.Bookkeeping
  }

  /**
   * Whether the selected inventory item type is consultation.
   */
  @computed
  get isConsultation() {
    return this.itemType === InventoryItemType.Consultation
  }

  /**
   * Whether the selected inventory item type is tax.
   */
  @computed
  get isTax() {
    return this.itemType === InventoryItemType.Tax
  }

  /**
   * Whether the selected inventory item type is extension.
   */
  @computed
  get isExtension() {
    return this.itemType === InventoryItemType.Extension
  }

  extensionVM = new ExtensionBillingViewModel()

  /**
   * Whether the selected inventory item type requires flat pricing.
   */
  @computed
  get requiresFlatPricing() {
    return !this.isDefault
  }

  /**
   * Whether the selected price type is Flat.
   */
  @computed
  get isFlatPriceType() {
    return this.itemPriceType === InventoryItemPricing.Flat
  }

  @computed
  get useRegularPricing() {
    return !this.isExtension
  }

  /**
   * Resets elements involved in the creation process
   */
  @action.bound
  reset() {
    this.name = ''
    this.description = ''
    this.courtesyQuantity = 0
    this.itemPriceType = InventoryItemPricing.Flat
    this.supportingItems = []
    this.hideFromPro = false
    this.consultationLength = 0
    this.reviewPrice = 0
    this.reviewPayout = 0
    this.itemType = InventoryItemType.Default
    this.recurring = false
    this.extensionVM.reset()

    this.submit.reset()
    this.validation.reset()
    this.pricingViewModel.reset()
  }

  @action.bound
  setInventoryItem(item: InventoryItem) {
    this.item = item
    this.name = item.name
    this.description = item.description
    this.courtesyQuantity = item.courtesyAmount
    this.supportingItems = item.supportingItems
    this.itemPriceType = item.priceType
    this.hideFromPro = item.hidden
    this.itemType = item.typeSpecification.type

    if (
      item.typeSpecification instanceof
      InventoryItemConsultationTypeSpecification
    ) {
      this.consultationLength = item.typeSpecification.lengthInMinutes
    }

    if (
      item.typeSpecification instanceof
      InventoryItemBookkeepingTypeSpecification
    ) {
      this.recurring = item.typeSpecification.recurring
    }

    if (item.typeSpecification instanceof InventoryItemTaxTypeSpecification) {
      this.reviewPrice = item.typeSpecification.reviewPrice
      this.reviewPayout = item.typeSpecification.reviewPayout
    }

    if (
      item.typeSpecification instanceof InventoryItemExtensionTypeSpecification
    ) {
      this.extensionVM.hydrate(item.typeSpecification)
    }

    this.pricingViewModel.hydrate(inventoryItemToPricingValues(item))
  }

  @action.bound
  setItemType(newItemType: InventoryItemType) {
    this.itemType = newItemType

    // Non-default types currently only support Flat pricing.
    if (this.requiresFlatPricing && !this.isFlatPriceType) {
      this.setItemPriceType(InventoryItemPricing.Flat)
    }
  }

  @action.bound
  setItemPriceType(newItemPriceType: InventoryItemPricing) {
    this.itemPriceType = newItemPriceType
    this.validation.reset()
    this.pricingViewModel.reset()
  }

  @action
  open(item: InventoryItem) {
    this.reset()
    this.setInventoryItem(item)
    this.show()
  }

  @action.bound
  setSupportingItems(supportingItems: InventoryItemSupportingItem[]) {
    this.supportingItems = supportingItems
  }

  @action.bound
  setHideFromPro(hideFromPro: boolean) {
    this.hideFromPro = hideFromPro
  }

  @action.bound
  setRecurring(recurring: boolean) {
    this.recurring = recurring
  }

  @action.bound
  validate(): boolean {
    // Both should run independently.
    const base = this.validation.reset().validate().isValid
    const pricing = this.pricingViewModel.validate()

    if (this.isExtension) {
      const extension = this.extensionVM.validate()

      return base && pricing && extension
    }

    return base && pricing
  }

  abstract submit: Task<[], Promise<void>>

  getInventoryItemTypeSpecification(): InventoryItemTypeSpecification {
    switch (this.itemType) {
      case InventoryItemType.Default:
        return new InventoryItemDefaultTypeSpecification()
      case InventoryItemType.Bookkeeping:
        return new InventoryItemBookkeepingTypeSpecification(this.recurring)
      case InventoryItemType.Consultation:
        return new InventoryItemConsultationTypeSpecification(
          this.consultationLength,
        )
      case InventoryItemType.Tax:
        return new InventoryItemTaxTypeSpecification(
          this.reviewPrice,
          this.reviewPayout,
        )
      case InventoryItemType.Extension:
        return new InventoryItemExtensionTypeSpecification(
          this.extensionVM.extensionType,
          this.extensionVM.billingType,
          this.extensionVM.billingVM.toValues(),
        )
    }
  }
}

/**
 * Maps from an existing Inventory Item to the values-representation of its pricing.
 */
function inventoryItemToPricingValues(
  item: InventoryItem,
): InventoryItemPricingValues {
  switch (item.priceType) {
    case InventoryItemPricing.Fee:
      return {
        type: InventoryItemPricing.Fee,
        fee: item.price,
      }
    case InventoryItemPricing.Flat:
      return {
        type: InventoryItemPricing.Flat,
        clientPrice: item.price,
        proPayoutAmount: item.proPrice,
      }
    case InventoryItemPricing.Markup:
      return {
        type: InventoryItemPricing.Markup,
        clientMarkupRate: item.price,
        proPayoutMarkupRate: item.proPrice,
      }
    case InventoryItemPricing.Markdown:
      return {
        type: InventoryItemPricing.Markdown,
        clientMarkdownRate: item.price,
        proPayoutMarkdownRate: item.proPrice,
      }
  }
}
